import React, {useState} from 'react';
import {StyleSheet, View} from 'react-native';
import {KeyboardAwareScrollView} from 'react-native-keyboard-aware-scroll-view';
import {observer} from 'mobx-react-lite';

import CouponModal from '../../../components/modals/CouponModal';
import {LastMinuteOptionsCarousel} from '../../../components/ProductCarousel';
import TitleLine from '../../../components/rewards/TitleLine';
import SafeAreaPadding from '../../../components/SafeAreaPadding';
import Button from '../../../components/themed/Button';
import ContentView from '../../../components/ui-controls/layout/ContentView';
import {useTabBarBottomPadding} from '../../../hooks/useTabBarBottomPadding';
import {useWebScreenDimensions} from '../../../hooks/useWebScreenDimensions';
import {AppRoute} from '../../../models/AppRoute';
import {RouteNavigatorScreenProps} from '../../../models/navigationTypes';
import {useStores} from '../../../stores/use-stores';
import CartCoupon from '../CartCoupon';
import CartItemsList from '../components/CartItemsList';
import CouponBlockedNotice from '../components/CouponBlockedNotice';
import OrderTotals from '../components/OrderTotals';
import PickupDeliveryDetails from '../components/PickupDeliveryDetails';
import {useCheckoutSession} from '../hooks';
import UnifiedCheckoutScreen from '../UnifiedCheckout';
import {handleContinue} from '../util/validation';

const CheckoutReviewScreen = observer(
  ({navigation}: RouteNavigatorScreenProps<AppRoute.CART_ORDER_CHECKOUT_REVIEW>) => {
    // ALL HOOKS MUST BE CALLED BEFORE ANY CONDITIONAL RETURNS
    // React requires hooks to be called in the same order on every render

    // Session management: login reload + empty cart redirect
    useCheckoutSession({step: 'review'});

    const {cartStore, userStore} = useStores();
    const {isWebDesktopScreen} = useWebScreenDimensions();
    const tabBarPadding = useTabBarBottomPadding();
    const [isCouponModalVisible, setIsCouponModalVisible] = useState(false);

    // Web desktop uses unified checkout layout
    if (isWebDesktopScreen) {
      return <UnifiedCheckoutScreen step="review" navigation={navigation} />;
    }

    // Mobile uses existing implementation below
    return (
      <ContentView testID="checkout-screen">
        <KeyboardAwareScrollView
          style={{backgroundColor: 'white'}}
          keyboardShouldPersistTaps="always"
          showsVerticalScrollIndicator={isWebDesktopScreen}
          showsHorizontalScrollIndicator={isWebDesktopScreen}
          contentContainerStyle={{gap: 16, paddingBottom: tabBarPadding}}
          enableOnAndroid
          extraScrollHeight={40}>
          <View style={styles.section} testID="checkout-screen-your-order">
            <TitleLine title="REVIEW YOUR ORDER" />
            <CartItemsList
              orderLines={cartStore.currentOrder?.order_lines}
              testID="review-screen-items-list"
            />
          </View>
          <PickupDeliveryDetails />
          <CartCoupon onCouponPress={() => setIsCouponModalVisible(true)} />
          <OrderTotals>
            <Button
              title={
                cartStore.couponBlocksCheckout
                  ? 'ADD MORE TO CONTINUE WITH COUPON'
                  : 'GO TO PAYMENT'
              }
              onPress={handleContinue({
                cartStore,
                userStore,
                skipServerValidation: true,
                callback: () => {
                  navigation.navigate(AppRoute.CART_ORDER_CHECKOUT_PAYMENT);
                },
              })}
              disabled={
                cartStore.submittingOrder ||
                cartStore.isLoadingOrder ||
                cartStore.couponBlocksCheckout
              }
              loading={cartStore.submittingOrder || cartStore.isLoadingOrder}
              testID="checkout-screen-place-order-button"
            />
            <CouponBlockedNotice />
          </OrderTotals>
          <LastMinuteOptionsCarousel />
        </KeyboardAwareScrollView>
        <CouponModal open={isCouponModalVisible} callback={() => setIsCouponModalVisible(false)} />
        <SafeAreaPadding bottom />
      </ContentView>
    );
  },
);

export default CheckoutReviewScreen;

const styles = StyleSheet.create({
  section: {
    marginTop: 8,
    gap: 8,
  },
});
